import React, { useCallback, useEffect, useMemo, useState } from "react";
import AuthService from "../services/auth.service";
import { useLocation, useNavigate } from "react-router";
import { AppRoutes } from "../config";
import LoadingScreen from "../components/LoadingScreen/LoadingScreen";
import { matchRoute } from "../config/app.routes";
import { User } from "../types";
import useMetaMask from "./useMetaMask";
import { consoleError, consoleLog } from "../utils/methods";
import { isGTA } from "../services/challenge.service";

export type AuthorizationContextType = {
  loggedIn: boolean;
  fetchingUser: boolean;
  user: User;
  setUser: (user: any) => void;
  setLogged: (flag: boolean) => void;
  setLoading: (flag: boolean) => void;
  fetchUser: () => Promise<void>;
  isTrust: boolean;
  isAdmin: boolean;
  isGlobalTrustAdmin: boolean;
  isPioneer: boolean;
  isGuardian: boolean;
};

export const AuthorizationContext = React.createContext<AuthorizationContextType | null>(null);

export const AuthorizationProvider = ({ children }: any) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { waitForReady, isSdkReady, account: sdkAccount, web3Provider } = useMetaMask();
  const [user, setUser] = useState<User | null>(null);

  const [fetchingUser, setLoading] = useState(false);
  const [loggedIn, setLogged] = useState(false);
  // Governance.isGTA(user.address) — null until read or if the read failed. V10.1 makes the GTA
  // registry the authority on who is a Global Trust Admin (panel seats, consortium votes); the
  // old "admin with no country code" heuristic is kept only as the fallback while this is null.
  const [chainIsGTA, setChainIsGTA] = useState<boolean | null>(null);

  useEffect(() => {
    const address = user?.address;
    if (!address || user?.role !== "admin") {
      setChainIsGTA(null);
      return;
    }
    let cancelled = false;
    isGTA(address)
      .then((v) => {
        if (!cancelled) {
          setChainIsGTA(v);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setChainIsGTA(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user?.address, user?.role]);


  useEffect(() => {
    consoleLog("Fetching user");
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    consoleLog(`Logged in`, loggedIn);
    if (!loggedIn) {
      setUser(null);
    }
  }, [loggedIn]);

  /**
   * Helper to get the currently selected account from MetaMask
   * Prefers the SDK-provided account; falls back to passive eth_accounts
   */
  const getSelectedAccount = async (): Promise<string | null> => {
    // SDK account is the most reliable source
    if (sdkAccount) return sdkAccount.toLowerCase();

    if (!web3Provider) return null;

    try {
      const accounts = await web3Provider.request({ method: "eth_accounts" }) as string[];
      return accounts && accounts.length > 0 ? accounts[0].toLowerCase() : null;
    } catch {
      return null;
    }
  };

  /**
   * Validates that the wallet account matches the authenticated user
   * Returns true if valid, false if account mismatch detected
   */
  const validateAccountMatch = (userAddress: string, walletAccount: string | null): boolean => {
    if (!walletAccount) {
      // No wallet account available yet - this is OK during initialization
      consoleLog("No wallet account available for validation - skipping check");
      return true;
    }

    const matches = userAddress.toLowerCase() === walletAccount.toLowerCase();
    if (!matches) {
      consoleError("Account mismatch detected", { userAddress, walletAccount });
    }
    return matches;
  };

  const fetchUser = useCallback(async () => {
    try {
      localStorage.removeItem("countries");
      setLoading(true);
      const route = matchRoute(location.pathname);

      consoleLog(AuthService.loggedIn() ? "Has token" : "No token");

      // If no token and route requires auth, redirect without trying to connect
      if (route && (route.requireUser || route.requireTrust) && !AuthService.loggedIn()) {
        AuthService.logout();
        navigate(AppRoutes.Home.path);
        setLogged(false);
        return setLoading(false);
      }

      consoleLog("Checking auth");
      const result = await AuthService.checkAuth();
      consoleLog(result ? "Has user" : "No user");

      // If no valid auth result, handle accordingly but don't force logout
      if (!result) {
        if (route && (route.requireUser || route.requireTrust)) {
          navigate(AppRoutes.Home.path);
        }
        AuthService.logout();
        setLogged(false);
        return setLoading(false);
      }

      // Wait for SDK to be ready before checking connection state
      consoleLog("Waiting for SDK to be ready...");
      await waitForReady(3000);
      consoleLog(`SDK ready state: ${isSdkReady ? "READY" : "NOT READY (proceeding anyway)"}`);

      // Get the current wallet account (passive check, no popup)
      const walletAccount = await getSelectedAccount();
      consoleLog("Wallet account from eth_accounts:", walletAccount || "none");

      // If we have a wallet account, validate it matches the authenticated user.
      // If we don't, trust the auth token and proceed — never force-connect just to
      // view a page. Signing actions (bids, claims, etc.) connect lazily on demand.
      if (walletAccount && !validateAccountMatch(result.address, walletAccount)) {
        consoleError("User switched wallet accounts - logging out");
        throw new Error("Account was changed since last login");
      }

      setLogged(true);
      setUser(result);

      if (route) {
        if (route.requireTrust) {
          if (!result.role || result.role !== "trust") {
            navigate(AppRoutes.Home.path);
          }
        } else if (route.requireUser) {
          if (!result.role) {
            navigate(AppRoutes.Profile.subRoutes.Dashboard.path);
          }
        }
      }

      setLoading(false);
      return;
    } catch (err: any) {
      // Only logout for genuine auth/account issues
      const isAccountMismatch = err?.message?.includes("Account was changed") ||
        err?.message?.includes("doesn't match authenticated");
      const isAuthRequired = err?.message?.includes("Failed to connect");

      if (isAccountMismatch || isAuthRequired) {
        consoleError("FetchUser error - logging out:", err?.message);
        AuthService.logout();
        navigate(AppRoutes.Home.path);
        setLogged(false);
      } else {
        // For other errors (network issues, etc.), don't immediately logout
        consoleError("FetchUser error (non-fatal):", err);
        // Still set logged in if we had a valid auth result earlier
        // This allows graceful degradation for temporary issues
      }

      setTimeout(() => {
        setLoading(false);
      }, 1000);
      return;
    }
  }, [location.pathname, navigate, waitForReady, sdkAccount, web3Provider]);

  const values = useMemo(
    () => ({
      user: user as User,
      isTrust: user?.role === "trust",
      isAdmin: user?.role === "admin",
      // "Global Trust Admin": V10.1 defines it by membership of the GTA registry in Governance
      // (V10_Accounts_Required §3 — the first holder "is not a substitute for the registry"), so
      // the chain answer wins. The onboarding-guide heuristic — any admin whose countryCode is
      // falsy — is the fallback while the read is in flight or unavailable. UI gating only; the
      // contracts enforce the real rule.
      isGlobalTrustAdmin: user?.role === "admin" && (chainIsGTA ?? !user?.countryCode),
      // Guardian onboarding has three states on an account:
      //   Patron   — role "user", email verified, no referrerAddress.
      //   Pioneer  — a Patron who redeemed a guardian invite code on the account page. The backend
      //              only sets referrerAddress (role stays "user"), so this is frontend-derived.
      //              Pioneers can upload and mint, but aren't labelled Guardians yet.
      //   Guardian — role "guardian", set by the backend's GuardianAdded handler once the Pioneer's
      //              first land mints (approveAndMint).
      // Gates for guardian *capabilities* check `isGuardian || isPioneer`; role labels/badges
      // tell the two apart.
      isPioneer:
        user?.role === "user" &&
        user?.referrerAddress != null &&
        user?.trustStatus === null &&
        user?.trusteeStatus === null &&
        user?.trustAdminStatus === null,
      isGuardian: user?.role === "guardian",
      loggedIn,
      fetchingUser,
      setUser,
      setLogged,
      setLoading,
      fetchUser,
    }),
    [user, loggedIn, fetchingUser, fetchUser, chainIsGTA]
  );

  return (
    <AuthorizationContext.Provider value={values}>
      <LoadingScreen loading={fetchingUser}>{children}</LoadingScreen>
    </AuthorizationContext.Provider>
  );
};

export default function useAuthorization(): AuthorizationContextType {
  const context = React.useContext(AuthorizationContext) as AuthorizationContextType;

  if (context === undefined) {
    throw new Error("useAuthorization hook must be used with a AuthorizationProvider component");
  }

  return context;
}
